import { useI18n as useVueI18n } from 'vue-i18n'
import { ref } from 'vue'
import { supportedLocales } from '@/locales'

export function useI18n() {
  const { t, locale, n, d } = useVueI18n({ useScope: 'global' })
  
  // Список поддерживаемых языков
  const availableLocales = ref(supportedLocales)
  
  // Функция для определения языка браузера
  const detectBrowserLanguage = (): string => {
    // Получаем язык из localStorage (если был сохранен)
    const savedLocale = localStorage.getItem('locale')
    if (savedLocale && savedLocale in supportedLocales) {
      return savedLocale
    }
    
    // Определяем язык браузера
    const browserLang = navigator.language || navigator.languages?.[0] || 'en'
    const shortLang = browserLang.split('-')[0].toLowerCase()
    
    // Проверяем, поддерживается ли язык браузера
    if (shortLang in supportedLocales) {
      return shortLang
    }
    
    // Если язык браузера не поддерживается, возвращаем русский по умолчанию
    return 'ru'
  }
  
  // Инициализация языка при загрузке
  const initializeLocale = () => {
    const detectedLocale = detectBrowserLanguage()
    if (detectedLocale !== locale.value) {
      changeLocale(detectedLocale)
    }
  }
  
  // Смена языка
  const changeLocale = (newLocale: string) => {
    if (newLocale in supportedLocales) {
      locale.value = newLocale
      localStorage.setItem('locale', newLocale)
      
      // Обновляем язык в документе
      document.documentElement.lang = newLocale
      
      // Отправляем событие для обновления компонентов
      window.dispatchEvent(new CustomEvent('localeChanged', { 
        detail: { locale: newLocale } 
      }))
    }
  }
  
  // Функция для установки языка из профиля пользователя
  const setUserLanguage = (userLanguage: string) => {
    if (userLanguage && userLanguage in supportedLocales) {
      changeLocale(userLanguage)
    }
  }
  
  // Форматирование валюты
  const formatCurrency = (amount: number | string, currency: string = 'KGS') => {
    // Преобразуем строку в число, если нужно
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    
    // Проверяем, что получили валидное число
    if (isNaN(numericAmount)) {
      return '0.00'
    }
    
    return n(numericAmount, { 
      style: 'currency', 
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }
  
  // Форматирование даты
  const formatDate = (date: Date | string, format: 'short' | 'long' = 'short') => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return d(dateObj, format)
  }
  
  // Форматирование числа
  const formatNumber = (number: number, options?: Intl.NumberFormatOptions) => {
    return n(number, options)
  }
  
  initializeLocale()
  
  return {
    t,
    locale,
    availableLocales,
    changeLocale,
    initializeLocale,
    setUserLanguage,
    formatCurrency,
    formatDate,
    formatNumber,
    n,
    d
  }
} 